import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/app/AppShell';
import { useEmployee, useUpdateEmployee, useTerminateEmployee, type Employee } from '@/lib/employees';
import { useDocuments } from '@/lib/documents';
import { useComplianceItems, complianceLabel } from '@/lib/compliance';
import {
  useRequests, useLeaveBalance, useAccrueLeave, useOvertimeYear, useRecordOvertime,
} from '@/lib/timeOff';
import { usePiiStore, usePiiReveal, usePiiErase, type PiiKind } from '@/lib/piiVault';
import { useGoals, useEmployeeReviews } from '@/lib/performance';
import { useDepartments } from '@/lib/departments';
import { countryFlag } from '@/lib/countries';
import { confirm, promptText } from '@/components/ui/ConfirmDialog';
import { CurrencyPicker } from '@/components/ui/CurrencyPicker';

const TABS = ['profile', 'compensation', 'pii', 'documents', 'timeOff', 'compliance', 'goals', 'reviews'] as const;
type Tab = typeof TABS[number];

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const employee = useEmployee(id);
  const [tab, setTab] = useState<Tab>('profile');

  if (employee.isLoading) {
    return <AppShell><div className="px-8 py-8 text-ink-500">{t('common.loading')}</div></AppShell>;
  }
  if (!employee.data) {
    return <AppShell><div className="px-8 py-8 text-ink-500">{t('employeeDetail.notFound', { defaultValue: 'Not found.' })}</div></AppShell>;
  }
  const e = employee.data;

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-6xl">
        <Link to="/app/people" className="text-xs text-accent-700 hover:text-accent-600 font-semibold">{t('employeeDetail.back')}</Link>
        <div className="flex flex-wrap items-end justify-between gap-3 mt-2">
          <div>
            <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{e.legalNameLocal}</h1>
            <p className="mt-1 text-sm text-ink-500">
              {countryFlag(e.countryCode)} {e.countryCode} · {e.employmentType} · {e.email}
              {e.jobTitle && <> · {e.jobTitle}</>}
            </p>
          </div>
          <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md ${
            e.status === 'active' ? 'bg-ok-50 text-ok-600' :
            e.status === 'on_leave' ? 'bg-warn-50 text-warn-600' :
            e.status === 'terminated' ? 'bg-ink-100 text-ink-400 line-through' :
            'bg-ink-100 text-ink-500'
          }`}>● {t(`status.${e.status}`)}</span>
        </div>

        <div className="mt-6 flex items-center gap-1 border-b border-ink-200 overflow-x-auto">
          {TABS.map((tk) => (
            <button key={tk} onClick={() => setTab(tk)} className={`px-4 py-2 text-sm font-semibold transition border-b-2 -mb-px whitespace-nowrap ${tab === tk ? 'border-accent-500 text-accent-700' : 'border-transparent text-ink-600 hover:text-ink-900'}`}>
              {t(`employeeDetail.tab_${tk === 'compensation' ? 'compensation' : tk === 'pii' ? 'pii' : tk === 'documents' ? 'documents' : tk === 'timeOff' ? 'timeOff' : tk}`, { defaultValue: tk })}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === 'profile' && <ProfileTab e={e} />}
          {tab === 'compensation' && <CompensationTab e={e} />}
          {tab === 'pii' && <PiiTab e={e} />}
          {tab === 'documents' && <DocsTab e={e} />}
          {tab === 'timeOff' && <TimeOffTab e={e} />}
          {tab === 'compliance' && <ComplianceTab e={e} />}
          {tab === 'goals' && <GoalsTab e={e} />}
          {tab === 'reviews' && <ReviewsTab e={e} />}
        </div>
      </div>
    </AppShell>
  );
}

function ProfileTab({ e }: { e: Employee }) {
  const { t } = useTranslation();
  const update = useUpdateEmployee();
  const terminate = useTerminateEmployee();
  const departments = useDepartments();
  const departmentName = e.departmentId ? departments.data?.find((d) => d.id === e.departmentId)?.name : null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    jobTitle: e.jobTitle ?? '',
    jobLevel: e.jobLevel ?? '',
    workLocation: e.workLocation ?? '',
    dateOfBirth: (e.dateOfBirth ?? '').toString().slice(0, 10),
    preferredName: e.preferredName ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    try {
      await update.mutateAsync({
        id: e.id,
        jobTitle: draft.jobTitle || undefined,
        jobLevel: draft.jobLevel || undefined,
        workLocation: draft.workLocation || undefined,
        dateOfBirth: draft.dateOfBirth || undefined,
        preferredName: draft.preferredName || undefined,
      });
      setEditing(false);
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }

  async function doTerminate() {
    const ok = await confirm({
      title: t('employeeDetail.terminateConfirmTitle', { defaultValue: 'Terminate employee?' }),
      description: t('employeeDetail.terminateConfirmBody', { defaultValue: 'This action will mark the employee as terminated. It cannot be undone without manual intervention.' }),
      tone: 'destructive',
      confirmLabel: t('employeeDetail.terminateConfirmLabel', { defaultValue: 'Continue' }),
    });
    if (!ok) return;
    const date = await promptText({
      title: t('employeeDetail.terminateDateTitle', { defaultValue: 'Termination date' }),
      description: t('employeeDetail.terminateDateBody', { defaultValue: 'Enter the last working day (YYYY-MM-DD).' }),
      inputLabel: t('employeeDetail.terminateDateLabel', { defaultValue: 'Date' }),
      placeholder: new Date().toISOString().slice(0, 10),
      defaultValue: new Date().toISOString().slice(0, 10),
      tone: 'destructive',
      confirmLabel: t('employeeDetail.terminateDateConfirm', { defaultValue: 'Next' }),
    });
    if (!date) return;
    const reason = await promptText({
      title: t('employeeDetail.terminateReasonTitle', { defaultValue: 'Reason for termination' }),
      description: t('employeeDetail.terminateReasonBody', { defaultValue: 'Optional — leave blank to skip.' }),
      inputLabel: t('employeeDetail.terminateReasonLabel', { defaultValue: 'Reason' }),
      placeholder: t('employeeDetail.terminateReasonPlaceholder', { defaultValue: 'e.g. resignation, end of contract…' }),
      multiline: true,
      tone: 'destructive',
      confirmLabel: t('employeeDetail.terminateSubmit', { defaultValue: 'Terminate' }),
    });
    try {
      await terminate.mutateAsync({ id: e.id, terminationDate: date, reason: reason ?? undefined });
    } catch (err: unknown) {
      setError(((err as Error).message));
    }
  }

  async function doSetStatus(status: 'on_leave' | 'active') {
    const isLeave = status === 'on_leave';
    const ok = await confirm({
      title: isLeave
        ? t('employeeDetail.setLeaveTitle', { defaultValue: 'Mark as on leave?' })
        : t('employeeDetail.reinstateTitle', { defaultValue: 'Reinstate to active?' }),
      description: isLeave
        ? t('employeeDetail.setLeaveBody', { defaultValue: 'The employee will be marked as on leave.' })
        : t('employeeDetail.reinstateBody', { defaultValue: 'The employee will be reinstated to active status.' }),
      confirmLabel: isLeave
        ? t('employeeDetail.setLeaveConfirm', { defaultValue: 'Mark on leave' })
        : t('employeeDetail.reinstateConfirm', { defaultValue: 'Reinstate' }),
    });
    if (!ok) return;
    try {
      await update.mutateAsync({ id: e.id, status });
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-ink-900">{t('employeeDetail.profileTitle')}</h2>
        {!editing ? (
          <div className="flex gap-2">
            <button onClick={() => setEditing(true)} className="px-3 py-1.5 rounded-md bg-ink-100 text-ink-700 text-xs font-semibold hover:bg-ink-200">{t('common.edit')}</button>
            {e.status === 'active' && (
              <button onClick={() => doSetStatus('on_leave')} className="px-3 py-1.5 rounded-md bg-warn-50 text-warn-600 text-xs font-semibold hover:bg-warn-500 hover:text-white">
                {t('employeeDetail.markOnLeave', { defaultValue: 'Mark on leave' })}
              </button>
            )}
            {e.status === 'on_leave' && (
              <button onClick={() => doSetStatus('active')} className="px-3 py-1.5 rounded-md bg-ok-50 text-ok-600 text-xs font-semibold hover:bg-ok-500 hover:text-white">
                {t('employeeDetail.reinstate', { defaultValue: 'Reinstate' })}
              </button>
            )}
            {(e.status === 'active' || e.status === 'on_leave') && (
              <button onClick={doTerminate} className="px-3 py-1.5 rounded-md bg-ink-100 text-ink-600 text-xs font-semibold hover:bg-rose-50 hover:text-rose-600">
                {t('employeeDetail.terminate', { defaultValue: 'Terminate' })}
              </button>
            )}
          </div>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { setEditing(false); setError(null); }} className="px-3 py-1.5 rounded-md text-ink-700 text-xs font-semibold hover:bg-ink-100">{t('common.cancel')}</button>
            <button onClick={save} disabled={update.isPending} className="px-3 py-1.5 rounded-md bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 disabled:opacity-50">{update.isPending ? t('common.saving') : t('common.save')}</button>
          </div>
        )}
      </div>
      <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Item label={t('employeeDetail.legalName', { defaultValue: 'Legal name' })} value={e.legalNameLocal} />
        {e.legalNameKana && <Item label="フリガナ" value={e.legalNameKana} />}
        {e.legalNameRoman && <Item label={t('employeeDetail.romaji', { defaultValue: 'Romaji' })} value={e.legalNameRoman} />}
        <Item label={t('employeeDetail.preferredName', { defaultValue: 'Preferred name' })} value={editing ? <input className="i" value={draft.preferredName} onChange={(ev) => setDraft({ ...draft, preferredName: ev.target.value })} /> : (e.preferredName ?? '—')} />
        <Item label={t('people.dialog_email')} value={e.email} />
        <Item label={t('people.dialog_country')} value={`${countryFlag(e.countryCode)} ${e.countryCode}`} />
        <Item label={t('people.dialog_hireDate')} value={String(e.hireDate).slice(0, 10)} />
        <Item label={t('people.dialog_dob')} value={editing ? <input type="date" className="i" value={draft.dateOfBirth} onChange={(ev) => setDraft({ ...draft, dateOfBirth: ev.target.value })} /> : (e.dateOfBirth ? String(e.dateOfBirth).slice(0, 10) : '—')} />
        <Item label={t('employeeDetail.jobTitle', { defaultValue: 'Job title' })} value={editing ? <input className="i" value={draft.jobTitle} onChange={(ev) => setDraft({ ...draft, jobTitle: ev.target.value })} /> : (e.jobTitle ?? '—')} />
        <Item label={t('employeeDetail.jobLevel', { defaultValue: 'Job level' })} value={editing ? <input className="i" value={draft.jobLevel} onChange={(ev) => setDraft({ ...draft, jobLevel: ev.target.value })} /> : (e.jobLevel ?? '—')} />
        <Item label={t('employeeDetail.department', { defaultValue: 'Department' })} value={departmentName ? <Link to={`/app/people?department=${e.departmentId}`} className="text-accent-600 hover:underline">{departmentName}</Link> : '—'} />
        <Item label={t('employeeDetail.workLocation', { defaultValue: 'Work location' })} value={editing ? <input className="i" value={draft.workLocation} onChange={(ev) => setDraft({ ...draft, workLocation: ev.target.value })} /> : (e.workLocation ?? '—')} />
        {e.terminationDate && <Item label={t('employeeDetail.terminationDate', { defaultValue: 'Termination date' })} value={String(e.terminationDate).slice(0, 10)} />}
        {e.terminationReason && <Item label={t('employeeDetail.terminationReason', { defaultValue: 'Termination reason' })} value={e.terminationReason} />}
      </dl>
      {error && <div className="mt-4 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <style>{`.i{border:1px solid #D1D5DB;border-radius:0.375rem;padding:0.25rem 0.5rem;font-size:0.85rem;width:100%;outline:none;}.i:focus{border-color:#1A1AFF;}`}</style>
    </div>
  );
}

function CompensationTab({ e }: { e: Employee }) {
  const { t } = useTranslation();
  const update = useUpdateEmployee();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    baseSalary: e.baseSalaryCents != null ? String(e.baseSalaryCents / 100) : '',
    salaryCurrency: e.salaryCurrency ?? '',
    salaryPeriod: e.salaryPeriod ?? 'monthly',
  });
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    try {
      await update.mutateAsync({
        id: e.id,
        baseSalaryCents: draft.baseSalary ? Math.round(Number(draft.baseSalary) * 100) : undefined,
        salaryCurrency: draft.salaryCurrency || undefined,
        salaryPeriod: draft.salaryPeriod as 'monthly' | 'annual' | 'hourly',
      });
      setEditing(false);
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-ink-900">{t('employeeDetail.compensationTitle', { defaultValue: 'Compensation' })}</h2>
        {!editing
          ? <button onClick={() => setEditing(true)} className="px-3 py-1.5 rounded-md bg-ink-100 text-ink-700 text-xs font-semibold hover:bg-ink-200">{t('common.edit')}</button>
          : <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-md text-ink-700 text-xs font-semibold hover:bg-ink-100">{t('common.cancel')}</button>
              <button onClick={save} disabled={update.isPending} className="px-3 py-1.5 rounded-md bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 disabled:opacity-50">{update.isPending ? t('common.saving') : t('common.save')}</button>
            </div>}
      </div>
      <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Item label={t('employeeDetail.baseSalary', { defaultValue: 'Base salary' })} value={editing
          ? <input type="number" min="0" className="i" value={draft.baseSalary} onChange={(ev) => setDraft({ ...draft, baseSalary: ev.target.value })} />
          : (e.baseSalaryCents != null ? `${e.salaryCurrency ?? 'USD'} ${(e.baseSalaryCents / 100).toLocaleString()}` : '—')} />
        <Item label={t('employeeDetail.currency', { defaultValue: 'Currency' })} value={editing
          ? <CurrencyPicker value={draft.salaryCurrency || 'USD'} onChange={(v) => setDraft({ ...draft, salaryCurrency: v })} />
          : (e.salaryCurrency ?? '—')} />
        <Item label={t('employeeDetail.period', { defaultValue: 'Period' })} value={editing
          ? <select className="i" value={draft.salaryPeriod} onChange={(ev) => setDraft({ ...draft, salaryPeriod: ev.target.value as 'monthly' | 'annual' | 'hourly' })}><option>monthly</option><option>annual</option><option>hourly</option></select>
          : e.salaryPeriod} />
      </dl>
      {error && <div className="mt-4 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <style>{`.i{border:1px solid #D1D5DB;border-radius:0.375rem;padding:0.25rem 0.5rem;font-size:0.85rem;width:100%;outline:none;}.i:focus{border-color:#1A1AFF;}`}</style>
    </div>
  );
}

function PiiTab({ e }: { e: Employee }) {
  const { t } = useTranslation();
  const kinds: { kind: PiiKind; label: string; last4: string | null; visible: boolean }[] = [
    { kind: 'my_number', label: t('employeeDetail.piiKind_my_number'), last4: e.myNumberLast4, visible: e.countryCode === 'JP' },
    { kind: 'ssn', label: t('employeeDetail.piiKind_ssn'), last4: e.ssnLast4, visible: e.countryCode === 'US' },
    { kind: 'uk_ni', label: t('employeeDetail.piiKind_uk_ni'), last4: e.ukNiLast4, visible: e.countryCode === 'GB' },
    { kind: 'de_tax_id', label: t('employeeDetail.piiKind_de_tax_id'), last4: null, visible: e.countryCode === 'DE' },
    { kind: 'bank_account', label: t('employeeDetail.piiKind_bank_account'), last4: e.bankAccountLast4, visible: true },
  ];
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <h2 className="text-base font-semibold text-ink-900">{t('employeeDetail.piiTitle')}</h2>
      <p className="mt-1 text-xs text-ink-500">{t('employeeDetail.piiSubtitle')}</p>
      <div className="mt-5 divide-y divide-ink-100">
        {kinds.filter((k) => k.visible).map((k) => <PiiRow key={k.kind} employeeId={e.id} kind={k.kind} label={k.label} last4={k.last4} />)}
      </div>
    </div>
  );
}

function PiiRow({ employeeId, kind, label, last4 }: { employeeId: string; kind: PiiKind; label: string; last4: string | null }) {
  const { t } = useTranslation();
  const store = usePiiStore();
  const reveal = usePiiReveal();
  const erase = usePiiErase();
  const [mode, setMode] = useState<'idle' | 'store' | 'reveal'>('idle');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function doStore() {
    setError(null);
    try {
      await store.mutateAsync({ employeeId, kind, plaintext: value });
      setValue('');
      setMode('idle');
      window.location.reload(); // refresh last4
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }
  async function doReveal() {
    setError(null);
    if (reason.trim().length < 10) { setError(t('employeeDetail.piiReasonMinChars', { defaultValue: 'Reason must be ≥ 10 chars.' })); return; }
    try {
      const r = await reveal.mutateAsync({ employeeId, kind, reason });
      setPlaintext(r.plaintext);
      setTimeout(() => setPlaintext(null), 20_000);
      setReason('');
      setMode('idle');
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }
  async function doErase() {
    const ok = await confirm({
      title: t('employeeDetail.piiEraseConfirmTitle', { label }),
      description: t('employeeDetail.piiEraseConfirmBody'),
      tone: 'destructive',
      confirmLabel: t('employeeDetail.piiErase'),
    });
    if (!ok) return;
    const r = await promptText({
      title: t('employeeDetail.piiEraseReasonTitle'),
      description: t('employeeDetail.piiEraseReasonBody'),
      inputLabel: t('employeeDetail.piiReason'),
      placeholder: t('employeeDetail.piiReasonPlaceholder'),
      minLength: 10,
      multiline: true,
      tone: 'destructive',
      confirmLabel: t('employeeDetail.piiErase'),
    });
    if (!r) return;
    try {
      await erase.mutateAsync({ employeeId, kind, reason: r });
      window.location.reload();
    } catch (err: unknown) {
      setError(((err as Error).message));
    }
  }

  return (
    <div className="py-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-ink-900">{label}</p>
          <p className="text-[11px] text-ink-500 num">{last4 ? `**** ${last4}` : t('employeeDetail.piiNoneStored')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMode(mode === 'store' ? 'idle' : 'store')} className="px-2.5 py-1 rounded-md bg-accent-50 text-accent-700 text-xs font-semibold hover:bg-accent-500 hover:text-white">{t('employeeDetail.piiStoreButton')}</button>
          {last4 && <button onClick={() => setMode(mode === 'reveal' ? 'idle' : 'reveal')} className="px-2.5 py-1 rounded-md bg-warn-50 text-warn-600 text-xs font-semibold hover:bg-warn-500 hover:text-white">{t('employeeDetail.piiRevealButton')}</button>}
          {last4 && <button onClick={doErase} className="px-2.5 py-1 rounded-md bg-ink-100 text-ink-600 text-xs font-semibold hover:bg-ink-300">{t('employeeDetail.piiEraseButton')}</button>}
        </div>
      </div>

      {mode === 'store' && (
        <div className="mt-3 rounded-lg bg-ink-50 p-3 space-y-2">
          <input type="text" value={value} onChange={(ev) => setValue(ev.target.value)} placeholder={t('employeeDetail.piiStoreDialog_valuePlaceholder')} className="i" />
          <p className="text-[10px] text-ink-500">{t('employeeDetail.piiStoreDialog_encryptedHint')}</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setMode('idle')} className="px-2.5 py-1 rounded-md text-ink-600 text-xs font-semibold hover:bg-ink-200">{t('common.cancel')}</button>
            <button onClick={doStore} disabled={!value || store.isPending} className="px-3 py-1 rounded-md bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 disabled:opacity-50">{store.isPending ? t('employeeDetail.piiStoreDialog_saving') : t('employeeDetail.piiStoreDialog_save')}</button>
          </div>
        </div>
      )}

      {mode === 'reveal' && (
        <div className="mt-3 rounded-lg bg-warn-50/30 border border-warn-500/30 p-3 space-y-2">
          <p className="text-[11px] text-ink-700">{t('employeeDetail.piiRevealDialog_reasonLabel')}</p>
          <textarea value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder={t('employeeDetail.piiRevealDialog_reasonPlaceholder')} className="i" rows={2} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setMode('idle')} className="px-2.5 py-1 rounded-md text-ink-600 text-xs font-semibold hover:bg-ink-200">{t('common.cancel')}</button>
            <button onClick={doReveal} disabled={reveal.isPending || reason.length < 10} className="px-3 py-1 rounded-md bg-warn-500 text-white text-xs font-semibold hover:bg-warn-600 disabled:opacity-50">{reveal.isPending ? t('employeeDetail.piiRevealDialog_revealing') : t('employeeDetail.piiRevealDialog_confirm')}</button>
          </div>
        </div>
      )}

      {plaintext && (
        <div className="mt-3 rounded-lg bg-ink-900 text-ink-50 p-3">
          <p className="text-[10px] text-accent-300 uppercase tracking-wide font-semibold">{t('employeeDetail.piiRevealDialog_result')}</p>
          <p className="num text-base font-mono mt-1">{plaintext}</p>
          <p className="text-[10px] text-ink-400 mt-1">{t('employeeDetail.piiRevealDialog_resultHint')}</p>
        </div>
      )}

      {error && <div className="mt-3 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-xs px-3 py-2">{error}</div>}

      <style>{`.i{width:100%;border:1px solid #D1D5DB;border-radius:0.375rem;padding:0.4rem 0.625rem;font-size:0.85rem;background:white;outline:none;}.i:focus{border-color:#1A1AFF;}`}</style>
    </div>
  );
}

function DocsTab({ e }: { e: Employee }) {
  const { t } = useTranslation();
  const docs = useDocuments({ employeeId: e.id });
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <h2 className="text-base font-semibold text-ink-900">{t('employeeDetail.documentsTitle', { defaultValue: 'Documents' })}</h2>
      <ul className="mt-4 divide-y divide-ink-100">
        {docs.data?.length === 0 && <li className="py-2 text-xs text-ink-500">{t('employeeDetail.noDocuments', { defaultValue: 'No documents.' })}</li>}
        {(docs.data ?? []).map((d) => (
          <li key={d.id} className="py-2 flex items-baseline justify-between">
            <div><p className="text-sm font-medium text-ink-900">{d.title}</p><p className="text-[10px] text-ink-500 font-mono">{d.templateKey} · {d.status}</p></div>
            <p className="text-[10px] text-ink-500">{new Date(d.createdAt).toLocaleDateString()}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TimeOffTab({ e }: { e: Employee }) {
  const { t } = useTranslation();
  const reqs = useRequests({ employeeId: e.id });
  const isJp = e.countryCode === 'JP';
  return (
    <div className="space-y-4">
      {isJp && <LeaveBalanceCard e={e} />}
      {isJp && <OvertimeCard e={e} />}
      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <h2 className="text-base font-semibold text-ink-900">{t('employeeDetail.timeOffRequests', { defaultValue: 'Time off requests' })}</h2>
        <ul className="mt-4 divide-y divide-ink-100">
          {reqs.data?.length === 0 && <li className="py-2 text-xs text-ink-500">{t('employeeDetail.noRequests', { defaultValue: 'No requests.' })}</li>}
          {(reqs.data ?? []).map((r) => (
            <li key={r.id} className="py-2 flex items-baseline justify-between">
              <p className="text-sm text-ink-900">{r.startDate.slice(0,10)} → {r.endDate.slice(0,10)}</p>
              <span className="text-[11px] font-medium text-ink-600">{r.status}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function LeaveBalanceCard({ e }: { e: Employee }) {
  const { t } = useTranslation();
  const bal = useLeaveBalance(e.id);
  const accrue = useAccrueLeave();
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-ink-900">有給休暇 / Paid leave (労基法 第39条)</h2>
        <button onClick={() => accrue.mutate(e.id)} disabled={accrue.isPending}
          className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-semibold hover:bg-ink-50 disabled:opacity-50">
          {accrue.isPending ? t('employeeDetail.accruing', { defaultValue: 'Accruing…' }) : t('employeeDetail.runAccrual', { defaultValue: 'Run accrual' })}
        </button>
      </div>
      {bal.data && (
        <>
          <div className="mt-4 flex gap-6">
            <Stat label="残日数 / Balance" value={`${bal.data.balanceDays} 日`} strong />
            <Stat label="付与(有効) / Granted" value={`${bal.data.grantedActiveDays} 日`} />
            <Stat label="取得済 / Used" value={`${bal.data.usedDays} 日`} />
          </div>
          <ul className="mt-4 divide-y divide-ink-100">
            {bal.data.grants.map((g) => (
              <li key={g.id} className="py-1.5 flex items-baseline justify-between text-xs">
                <span className="text-ink-700">{g.grantedOn} · {g.days}日 ({g.tenureMonths}ヶ月)</span>
                <span className="text-ink-400">{t('employeeDetail.expires', { defaultValue: 'expires {{date}}', date: g.expiresOn })}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function OvertimeCard({ e }: { e: Employee }) {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const ot = useOvertimeYear(e.id, year);
  const rec = useRecordOvertime();
  const [month, setMonth] = useState(1);
  const [hours, setHours] = useState('');
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <h2 className="text-base font-semibold text-ink-900">時間外労働 / Overtime — 36協定 ({year})</h2>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-xs text-ink-600">{t('employeeDetail.month', { defaultValue: 'Month' })}
          <select value={month} onChange={(ev) => setMonth(Number(ev.target.value))} className="ms-1 h-9 rounded-lg border border-ink-200 px-2 text-sm">
            {Array.from({ length: 12 }, (_, i) => <option key={i+1} value={i+1}>{i+1}</option>)}
          </select>
        </label>
        <input value={hours} onChange={(ev) => setHours(ev.target.value)} inputMode="decimal" placeholder={t('employeeDetail.hoursPlaceholder', { defaultValue: 'hours' })}
          className="h-9 w-24 rounded-lg border border-ink-200 px-2 text-sm" />
        <button
          onClick={() => { if (hours) rec.mutate({ employeeId: e.id, year, month, overtimeHours: Number(hours) }); setHours(''); }}
          disabled={rec.isPending || !hours}
          className="rounded-lg bg-accent-500 text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90 disabled:opacity-50">
          {t('employeeDetail.record', { defaultValue: 'Record' })}
        </button>
        {ot.data && <span className="ms-auto text-xs text-ink-600">年間合計 / Annual: <b>{ot.data.totalOvertimeHours}h</b></span>}
      </div>
      {ot.data && (ot.data.monthlyViolations.length > 0 || ot.data.yearlyViolations.length > 0) && (
        <ul className="mt-3 space-y-1">
          {[...ot.data.monthlyViolations, ...ot.data.yearlyViolations].map((v, i) => (
            <li key={i} className={`text-xs rounded-lg px-2 py-1 ${v.severity === 'violation' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
              {v.month ? `M${v.month}: ` : ''}{v.message}
            </li>
          ))}
        </ul>
      )}
      {ot.data && (
        <div className="mt-3 grid grid-cols-6 gap-1 text-center text-[11px]">
          {ot.data.records.map((r) => (
            <div key={r.id} className={`rounded px-1 py-1 ${r.overtimeHours > 45 ? 'bg-amber-100 text-amber-800' : 'bg-ink-50 text-ink-600'}`}>
              {r.month}月<br/><b>{r.overtimeHours}h</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-400">{label}</div>
      <div className={`${strong ? 'text-xl font-bold' : 'text-base font-semibold'} text-ink-900 num`}>{value}</div>
    </div>
  );
}

function GoalsTab({ e }: { e: Employee }) {
  const { t } = useTranslation();
  const goals = useGoals(e.id);

  const statusColor: Record<string, string> = {
    active: 'bg-accent-50 text-accent-700',
    on_track: 'bg-ok-50 text-ok-600',
    at_risk: 'bg-warn-50 text-warn-600',
    done: 'bg-ink-100 text-ink-500',
    dropped: 'bg-ink-100 text-ink-400 line-through',
  };

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <h2 className="text-base font-semibold text-ink-900">{t('employeeDetail.goalsTitle', { defaultValue: 'Goals' })}</h2>
      <p className="mt-1 text-xs text-ink-500">{t('employeeDetail.goalsSubtitle', { defaultValue: 'Performance goals assigned to this employee.' })}</p>
      {goals.isLoading && <p className="mt-4 text-xs text-ink-500">{t('common.loading')}</p>}
      <ul className="mt-4 divide-y divide-ink-100">
        {!goals.isLoading && (goals.data ?? []).length === 0 && (
          <li className="py-2 text-xs text-ink-500">{t('employeeDetail.goalsEmpty', { defaultValue: 'No goals yet.' })}</li>
        )}
        {(goals.data ?? []).map((g) => (
          <li key={g.id} className="py-3 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink-900 truncate">{g.title}</p>
              {g.description && <p className="text-[11px] text-ink-500 mt-0.5 truncate">{g.description}</p>}
              <div className="mt-1 flex items-center gap-2">
                {g.dueOn && <span className="text-[10px] text-ink-400">{t('employeeDetail.goalsDue', { defaultValue: 'Due' })} {String(g.dueOn).slice(0, 10)}</span>}
                {g.metric && <span className="text-[10px] text-ink-400 truncate">· {g.metric}</span>}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <div className="h-1.5 w-24 rounded-full bg-ink-100 overflow-hidden">
                  <div className="h-full rounded-full bg-accent-500" style={{ width: `${g.progress}%` }} />
                </div>
                <span className="text-[10px] text-ink-500 num">{g.progress}%</span>
              </div>
            </div>
            <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-md shrink-0 ${statusColor[g.status] ?? 'bg-ink-100 text-ink-500'}`}>
              {g.status}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReviewsTab({ e }: { e: Employee }) {
  const { t } = useTranslation();
  const reviews = useEmployeeReviews(e.id);

  const kindColor: Record<string, string> = {
    self: 'bg-accent-50 text-accent-700',
    manager: 'bg-ok-50 text-ok-600',
    peer: 'bg-ink-100 text-ink-600',
    skip_level: 'bg-warn-50 text-warn-600',
  };

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <h2 className="text-base font-semibold text-ink-900">{t('employeeDetail.reviewsTitle', { defaultValue: 'Performance Reviews' })}</h2>
      <p className="mt-1 text-xs text-ink-500">{t('employeeDetail.reviewsSubtitle', { defaultValue: 'All reviews where this employee is the subject.' })}</p>
      {reviews.isLoading && <p className="mt-4 text-xs text-ink-500">{t('common.loading')}</p>}
      <ul className="mt-4 divide-y divide-ink-100">
        {!reviews.isLoading && (reviews.data ?? []).length === 0 && (
          <li className="py-2 text-xs text-ink-500">{t('employeeDetail.reviewsEmpty', { defaultValue: 'No reviews yet.' })}</li>
        )}
        {(reviews.data ?? []).map((r) => (
          <li key={r.id} className="py-3 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-md ${kindColor[r.kind] ?? 'bg-ink-100 text-ink-500'}`}>{r.kind}</span>
                <span className="text-[10px] text-ink-400 font-mono">{r.status}</span>
                {r.rating != null && (
                  <span className="text-[10px] text-ink-600 font-semibold num">★ {r.rating}</span>
                )}
              </div>
              {r.strengths && <p className="text-xs text-ink-700 mt-1"><span className="font-semibold">{t('employeeDetail.reviewStrengths', { defaultValue: 'Strengths' })}:</span> {r.strengths}</p>}
              {r.growthAreas && <p className="text-xs text-ink-700 mt-0.5"><span className="font-semibold">{t('employeeDetail.reviewGrowth', { defaultValue: 'Growth areas' })}:</span> {r.growthAreas}</p>}
              {r.comments && <p className="text-xs text-ink-500 mt-0.5 italic">{r.comments}</p>}
            </div>
            <p className="text-[10px] text-ink-400 shrink-0">{r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : '—'}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ComplianceTab({ e }: { e: Employee }) {
  const { t, i18n } = useTranslation();
  const items = useComplianceItems();
  const my = (items.data ?? []).filter((i) => i.employeeId === e.id);
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <h2 className="text-base font-semibold text-ink-900">{t('employeeDetail.complianceTitle', { defaultValue: 'Compliance' })}</h2>
      <ul className="mt-4 divide-y divide-ink-100">
        {my.length === 0 && <li className="py-2 text-xs text-ink-500">{t('employeeDetail.noComplianceItems', { defaultValue: 'No compliance items.' })}</li>}
        {my.map((i) => (
          <li key={i.id} className="py-2 flex items-baseline justify-between">
            <p className="text-sm text-ink-900">{complianceLabel(i.kind, i18n.language)}</p>
            <p className="text-[11px] text-ink-500">{i.dueDate ? String(i.dueDate).slice(0,10) : '—'} · {i.status}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Item({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="border-b border-ink-100 pb-2">
      <dt className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">{label}</dt>
      <dd className="text-sm text-ink-900 mt-0.5">{value ?? '—'}</dd>
    </div>
  );
}
